import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../LanguageContext";

const GALLERY_IMAGES = [
  // 1. Main facade
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/rczgcggf_torreamat-gal3.jpg",
    alt: "Torre Amat — fachada con hiedra y naranjos",
  },
  // 2-6. Interior photos right after the facade
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/rlx4orj7_Interior%20Galeria.jpeg",
    alt: "Interior galería — pasillos con azulejos y arte",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/tnhyd6it_Interior%20Sala%20Noble.jpeg",
    alt: "Interior Sala Noble",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/yhvb6sqn_Interior%20entrada.jpeg",
    alt: "Interior entrada",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/b1lw7vun_Interior.jpeg",
    alt: "Interior — galería de arte y azulejos",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/65k2vidt_LirolaCusso_LaCoqueteria_shootingnovias_web_346.jpg",
    alt: "Interior — mesa de honor con luz natural",
  },
  // 7+. Exteriors, garden and event photos
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/7w8s03u8_LirolaCusso_LaCoqueteria_shootingnovias_web_225.jpg",
    alt: "Boda en Torre Amat — novia en el jardín bajo los árboles",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/xtkzxiwf_TorreAmat_Restaurant_-2.jpg",
    alt: "Banquete al aire libre bajo los árboles",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/wz869ms1_Galeria%20exterior.jpg",
    alt: "Torre Amat galería exterior",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/fwpwl91g_LirolaCusso_LaCoqueteria_shootingnovias_web_347.jpg",
    alt: "Jardín — barandilla de hierro forjado con vista al jardín",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/6cwcdaej_torre-amat-lo-ultimo-en-barcelona-revista-80.jpg",
    alt: "Jardín con sillas de hierro",
  },
];

export default function GaleriaSection() {
  const { t } = useLang();
  const [lightbox, setLightbox] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((p) => (p + 1) % GALLERY_IMAGES.length);
      if (e.key === "ArrowLeft") setLightbox((p) => (p - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <section
      id="galeria"
      ref={ref}
      data-testid="galeria-section"
      className="bg-[#1A1A18] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <p
            className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.galeria.caption}
          </p>
          <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-8" />
          <h2
            className="fade-in-item text-4xl md:text-5xl font-light text-[#F5F0E8] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t.galeria.heading}
          </h2>
        </div>

        {/* Masonry-style bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {GALLERY_IMAGES.map((img, i) => (
            <div
              key={i}
              data-testid={`gallery-item-${i}`}
              className={`fade-in-item cursor-pointer overflow-hidden group relative ${
                i === 0 ? "md:col-span-2 md:row-span-2" : ""
              } ${i === 3 ? "md:col-span-2" : ""}`}
              style={{ animationDelay: `${i * 80}ms` }}
              onClick={() => setLightbox(i)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                  i === 0 ? "h-[400px] md:h-full" : "h-48 md:h-56"
                } ${i === 3 ? "h-48 md:h-52" : ""}`}
              />
              <div className="absolute inset-0 bg-[#1A1A18]/0 group-hover:bg-[#1A1A18]/20 transition-colors duration-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-[#1A1A18]/95 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors"
          >
            <X size={28} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((p) => (p - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length); }}
            className="absolute left-4 md:left-8 text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors"
          >
            <ChevronLeft size={36} />
          </button>
          <img
            src={GALLERY_IMAGES[lightbox].src}
            alt={GALLERY_IMAGES[lightbox].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); setLightbox((p) => (p + 1) % GALLERY_IMAGES.length); }}
            className="absolute right-4 md:right-8 text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors"
          >
            <ChevronRight size={36} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {GALLERY_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setLightbox(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === lightbox ? "bg-[#C8A96E]" : "bg-[#F5F0E8]/30"}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
